/* eslint-disable @typescript-eslint/ban-types */
import {
  TrackingElementContext,
  TrackingProduct,
  TrackingProductCategory,
  TrackingSectionContext,
  TrackingVariant,
} from '../../..//types';
import {getSourceCategory} from './category';
import {
  DataLayerProductGA4,
  TrackingClientState,
  getFilterString,
} from './clientState';
import {getTrackingPriceDataFromProduct} from './prices';
import {
  SourceHistoryEventData,
  getSourceHistoryEventData,
} from './sourceHistory';
import {stringifyEventData} from './stringifyEventData';

export const productDimensionMapping: Record<string, string> = {
  productAvailability: 'dimension53',

  merchantId: 'dimension67',
  categoryId: 'dimension66',
  sourceCategory: 'dimension68',
  sourceCategoryId: 'dimension69',

  strikeOutPrice: 'dimension114',

  sectionIndexOnPage: 'dimension106',
  rowIndexInSection: 'dimension108',
  columnIndexInSection: 'dimension110',

  isInSale: 'dimension70',
  saleAmount: 'dimension71',
  isSoldOut: 'dimension113',

  initiallyVisibleColumns: 'dimension73',
  source: 'dimension74',
  sourceHistoryOne: 'dimension75',
  sourceHistoryTwo: 'dimension76',
  sourceHistoryThree: 'dimension77',

  destinationUrl: 'dimension78',

  fitAnalyticsUsed: 'dimension54',
  sort: 'dimension55',
  filter: 'dimension112',

  masterKey: 'dimension119',
  sponsored: 'dimension120',
  supplierId: 'dimension121',
  sizeLabel: 'dimension122',

  price_bi: 'dimension26',
};

// eslint-disable-next-line @typescript-eslint/ban-types
export type MappedDimensionsProduct<T> = {} & {
  id: number;
  sizeLabel: string;
  price: number;
  readonly ____tagMappedDimensionsProductT: T;
};

export type ProductTrackingContextType =
  | {element: TrackingElementContext; section: TrackingSectionContext}
  | {element: undefined; section: undefined};

type OptionalTrackingProductCategory =
  | TrackingProductCategory
  | {
      path: string | null;
      id: number | null;
    };

// eslint-disable-next-line complexity
export const createDataLayerProduct = ({
  product,
  quantity = 1,
  variantId,
  category,
  price,
  tax,
  saleAmount,
  positionData = {
    position: 1,
    sectionIndexOnPage: 1,
    rowIndexInSection: 1,
    columnIndexInSection: 1,
  },
  sourceHistory,
  sort,
  sortChannel,
  filter,
  fitAnalyticsUsed,
  supplierId,
  sizeLabel,
  unitPrice,
}: {
  product: TrackingProduct;
  quantity?: number;
  variantId?: number;
  category: OptionalTrackingProductCategory | null;
  sourceCategory: OptionalTrackingProductCategory | null;
  price: number;
  strikeOutPrice: string | null;
  tax: number;
  saleAmount: number;
  positionData?: {
    position: number;
    sectionIndexOnPage: number;
    rowIndexInSection: number;
    columnIndexInSection: number;
    initiallyVisibleColumns?: number;
  };
  sourceHistory: SourceHistoryEventData;
  sort: string;
  sortChannel?: string;
  filter: string;
  fitAnalyticsUsed: string;
  supplierId?: number;
  sizeLabel?: string;
  unitPrice: TrackingProduct['unitPrice'];
}): {
  dataLayerProduct: DataLayerProductGA4;
} => {
  const placement = sourceHistory.source?.split('|')[3] ?? '';
  const priceTracker = product.price?.tracker;

  const priceBI = unitPrice || product.minPrice;
  const amount =
    product.price && 'price' in product.price
      ? product.price.price.amount
      : product.price?.tracker.fullPrice;

  // price_bi data order: <display_price>|<sale_discount>|<campaign_discount>|<original_price>|<voucherReduction>|<coupon_reduction>|<coins_reduction>
  const price_bi = stringifyEventData([
    amount || priceBI.withTax, // display_price
    priceBI.saleReductionWithTax, // sale price
    priceBI.campaignReductionWithTax, // campaign discount
    priceBI.fullPrice, // original price
    priceBI?.voucherReductionWithTax ?? 0, // voucher
    (priceBI.couponReductionWithTax || priceTracker?.couponReduction) ?? 0, // coupon reduction
    (priceBI.coinsReductionWithTax || priceTracker?.coinsReduction) ?? 0, // coins reduction
  ]);

  const dataLayerProduct: DataLayerProductGA4 = {
    item_id: String(product.id),
    item_name: product.name,
    price: Number(price.toFixed(2)),
    currency: product.minPrice.currency,
    sale_discount: Number(saleAmount.toFixed(2)),
    campaign_discount: Number(
      (product.minPrice.campaignReductionWithTax / 100).toFixed(2)
    ),
    original_price: priceBI.fullPrice
      ? parseFloat((priceBI.fullPrice / 100).toFixed(2))
      : null,
    price_bi,
    sold_out: product.isSoldOut,
    item_brand: product.brand.name,
    item_brand_id: product.brand.id.toString(),
    item_category: category && category.path,
    item_category_id: category && category.id,
    item_variant: variantId ? String(variantId) : '',
    item_badge: product.badges.join('|'),
    quantity,
    item_list_name: '',
    item_list_id: '',
    index: positionData.position,
    creative_name: product?.creativeName || '',
    promotion_id: product?.promotionId || '',
    sponsored: stringifyEventData([
      sortChannel,
      product.isSponsored,
      product.sponsoredType || '',
    ]),
    tax: Number(tax.toFixed(2)),

    sort,
    filter,
    // TBD
    fit_analytics_used: fitAnalyticsUsed,

    ...(supplierId ? {supplier_id: String(supplierId)} : {}),
    ...(sizeLabel ? {size_label: sizeLabel} : {}),
    master_key: product.masterKey,
    merchant_id: product.merchantId ? product.merchantId : null,
    position_bi: stringifyEventData([
      positionData.sectionIndexOnPage || 0,
      positionData.rowIndexInSection || 0,
      positionData.columnIndexInSection || 0,
      positionData.initiallyVisibleColumns || 0,
    ]),
    item_list_index: positionData.rowIndexInSection || 0,
    source: sourceHistory.source,
    location_id: placement,
    destination: stringifyEventData(['adp', product.id]),
    destination_url: product.url,
    linktarget_id: stringifyEventData(['adp', product.id]),
    linktarget: product.url,
  };

  return {
    dataLayerProduct,
  };
};

export function getStateFullDataLayerProduct({
  currentState,
  variant,
  product,
  element,
  section,
  quantity,
  sortChannel,
  category,
  productUnitPrice,
}: {
  currentState: TrackingClientState;
  variant?: TrackingVariant;
  product: TrackingProduct;
  quantity?: number;
  sortChannel?: string | undefined;
  category: OptionalTrackingProductCategory;
  productUnitPrice: TrackingProduct['unitPrice'];
} & ProductTrackingContextType) {
  const sourceCategory = getSourceCategory(currentState);

  const {price, saleAmount, strikeOutPrice, tax} =
    getTrackingPriceDataFromProduct(variant ? variant.price : product.minPrice);

  const positionDataObject = getPosition({
    element,
    section,
  } as ProductTrackingContextType);

  const {dataLayerProduct} = createDataLayerProduct({
    category,
    filter: getFilterString(currentState),
    fitAnalyticsUsed: currentState.fitAnalyticsUsed,
    positionData: positionDataObject,
    price,
    product,
    quantity,
    saleAmount,
    sort: currentState.sort,
    sortChannel,
    sourceCategory,
    sourceHistory: getSourceHistoryEventData({
      state: currentState,
      currentSourceSection: section,
    }),
    strikeOutPrice,
    tax,
    variantId: variant?.id,
    unitPrice: productUnitPrice,
  });

  return {
    product: dataLayerProduct,
  };
}

function getPosition({element, section}: ProductTrackingContextType) {
  if (element !== undefined && section !== undefined) {
    return {
      position:
        element.type === 'element-context'
          ? element.elementIndexInSection + 1
          : element.productIndexOnPage + 1,
      sectionIndexOnPage: section.sectionIndexOnPage + 1,
      rowIndexInSection: element.rowIndexInSection + 1,
      columnIndexInSection: element.columnIndexInSection + 1,

      initiallyVisibleColumns:
        section.type === 'reco' ? section.initiallyVisibleColumns : 1,
    };
  } else {
    return {
      position: 1,
      sectionIndexOnPage: 1,
      rowIndexInSection: 1,
      columnIndexInSection: 1,
    };
  }
}
